/* eslint-disable default-case */
import styled, { css } from "styled-components";
import React, { useState, useEffect } from "react";
import { useDaumPostcodePopup } from "react-daum-postcode";
import { useAirtable } from "../hooks/useAirtable";
import { useIframe } from "../hooks/useIframe";
import { useMediaQuery } from "react-responsive";

export const RegisterForm = ({
  url,
  mode,
  category,
  name,
  password,
  phoneNumber,
  domain,
}) => {
  const [dataState, setDataState] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [title, setTitle] = useState("개인참가신청");
  const [loading, setLoading] = useState(true);

  const isMobile = useMediaQuery({ maxWidth: 820 });

  const { fetchData, updateData, createData } = useAirtable(category);
  const { setMoveUrl } = useIframe(domain);

  const [dataId, setDataId] = useState("");
  const open = useDaumPostcodePopup();

  useEffect(() => {
    setTitle(
      category === "individual"
        ? "개인참가신청"
        : category === "group"
        ? "단체참가신청"
        : category === "club"
        ? "대항전참가신청"
        : "개인참가신청"
    );
  }, []);

  useEffect(() => {
    let fields = [
      {
        type: "text",
        name: "name",
        required: true,
        label: "성명",
        note: "※ 실명으로 입력, 띄어쓰기 금지",
        placeholder: "성명",
      },
      {
        type: "date",
        name: "birthdate",
        required: true,
        label: "생년월일",
        note: "※ 신분증에 기재된 생년월일 입력, 추후 수정 불가",
      },
      {
        type: "radio",
        name: "gender",
        options: ["남자", "여자"],
        required: true,
        label: "성별구분",
      },
      {
        type: "radio",
        name: "age",
        options: ["일반", "학생(초, 중, 고)"],
        required: true,
        label: "연령구분",
      },
      {
        type: "radio",
        name: "event",
        options: ["5km", "10km", "하프"],
        required: true,
        label: "참가부문",
      },
      {
        type: "radio",
        name: "gift",
        options: ["80", "85", "90", "95", "100", "105", "110"],
        required: true,
        label: "기념품",
      },
      {
        type: "tel",
        name: "phoneNumber",
        required: true,
        label: "휴대폰",
        note: "※ 연락 가능한 번호를 꼭 입력해주세요",
        placeholder: "010-0000-0000",
      },
      {
        type: "text",
        name: "postalCode",
        required: true,
        label: "우편번호",
      },
      { type: "text", name: "address", required: true, label: "주소" },
      {
        type: "text",
        name: "detailedAddress",
        required: true,
        label: "상세주소",
      },
      {
        type: "email",
        name: "email",
        required: false,
        label: "E-MAIL",
        placeholder: "이메일을 입력해주세요",
      },
      {
        type: "text",
        name: "depositor",
        required: true,
        label: "입금자명",
      },
      {
        type: "password",
        name: "password",
        required: true,
        label: "비밀번호",
        note: "※ 조회, 수정 시 필요합니다",
      },
    ];
    setFormFields(fields);
  }, [category]);

  useEffect(() => {
    const fetchDataAndSetData = async () => {
      const { fields, id } = await fetchData(
        `AND({name}="${name}", {password}="${password}", {phoneNumber}="${phoneNumber}")`
      );
      if (fields) {
        setDataState(fields);
        setDataId(id);
      } else {
        alert("죄송합니다, 잘못된 접근입니다.\n홈페이지로 돌아갑니다.");
        setMoveUrl(`${domain}/indiv-check`);
      }
    };

    if (mode === "edit") {
      fetchDataAndSetData();
    } else {
      setDataState({});
    }
    setLoading(false);
  }, [mode]);

  const handleInputChange = (event) => {
    let { name, value } = event.target;
    if (name === "phoneNumber") {
      value = formatPhoneNumber(value);
    }
    setDataState((prevState) => ({ ...prevState, [name]: value }));
  };

  const formatPhoneNumber = (value) => {
    const MAX_PHONE_LENGTH = 13; // "XXX-XXXX-XXXX" is 13 characters

    // Step 1: Remove any non-digit characters
    const numbers = value.replace(/[^\d]/g, "");

    // Step 2: Take the first 11 digits (if there are more)
    const trimmedNumbers = numbers.slice(0, 11);

    // Step 3: Divide the number string into 3-4-4
    const match = trimmedNumbers.match(/^(\d{3})(\d{4})(\d{4})$/);

    if (match) {
      const formattedNumber = `${match[1]}-${match[2]}-${match[3]}`;
      return formattedNumber.slice(0, MAX_PHONE_LENGTH); // ensure that the final string doesn't exceed the maximum length
    }

    return value; // if it doesn't match the pattern, return the original value
  };

  const checkRequiredFields = () => {
    const requiredFields = formFields
      .filter((field) => field.required)
      .map((field) => field.name);
    const isValid = requiredFields.every((name) => {
      if (dataState[name]) {
        return dataState[name].trim() !== "";
      }
      return false;
    });

    setIsValid(isValid);
  };

  const handleDaumComplete = (data) => {
    setDataState((prevState) => ({
      ...prevState,
      postalCode: data.zonecode,
      address: data.roadAddress,
      detailedAddress: "",
    }));
  };

  const handleDaumClick = () => {
    open({ onComplete: handleDaumComplete });
  };

  const handleCancelClick = () => {
    setMoveUrl(`${domain}`);
  };

  const extractRelevantFields = (item, requiredFields) => {
    let extractedData = {};
    requiredFields.forEach((field) => {
      extractedData[field] = item[field];
    });
    return extractedData;
  };

  const handleSubmit = async () => {
    if (isValid) {
      // Ensure that category is present before making any operation
      await sendSlackMessage(`Clicked from ${url}`);
      if (!category) {
        console.error("Category is missing!");
        return;
      }
      if (loading) {
        alert("현재 신청서를 제출입니다. 잠시만 기다려주세요🙏");
        return;
      }
      try {
        let responseData;
        setLoading(true);

        // Check if the mode is "apply" or "edit"
        if (mode === "apply") {
          // Using createData for "apply" mode
          responseData = await createData(dataState);
          setMoveUrl(`${domain}`); // notice로 넘기기
        } else if (mode === "edit") {
          // Ensure recordId is present for "edit" mode
          if (!dataId) {
            console.error("recordId is missing for edit mode!");
            return;
          }

          const requiredFields = [
            "name",
            "birthdate",
            "gender",
            "age",
            "event",
            "gift",
            "phoneNumber",
            "address",
            "postalCode",
            "detailedAddress",
            "email",
            "depositor",
            "password",
          ];
          const newData = extractRelevantFields(dataState, requiredFields);

          // Using updateData for "edit" mode
          responseData = await updateData(dataId, newData);
          setMoveUrl(`${domain}`); // notice로 넘기기
        } else {
          throw new Error("Invalid mode provided");
        }
        setLoading(false);
        // Handle successful operation, e.g.:
        // Show a success message, or redirect the user to another page.
      } catch (error) {
        console.error("Failed to process data:", error);
        const message = `현재 네트워크 접속상태가 좋지 않습니다.\n\n스마트폰 등 모바일 기기로 재신청 부탁드립니다.\n\n모바일에서도 동일한 에러가 발생한다면 개발지원팀(010-8793-8337) 으로 문의주시면 감사하겠습니다.`;
        alert(message);
        await sendSlackMessage(
          `FAILED! An error occurred from ${url}\n\n${error}`
        );

        // Handle error, e.g.:
        // Show an error message to the user.
      }
    } else {
      alert("참가신청서를 모두 입력해주세요.");
    }
  };

  const sendSlackMessage = async (message = "user trying submission") => {
    const userAgent = navigator.userAgent;
    const slackMessage = {
      text: `📌  ${message}\n\n${JSON.stringify(dataState)}\n\n${userAgent}`,
    };

    const url = `https://hooks.zapier.com/hooks/catch/16380552/35grui9/`;

    const data = {
      text: slackMessage,
    };

    fetch(url, {
      method: "POST",
      body: JSON.stringify(data),
    });
  };

  useEffect(() => {
    checkRequiredFields();
  }, [dataState]);
  // console.log("testing...");
  return (
    <Container ismobile={isMobile}>
      <Title ismobile={isMobile}>{title}</Title>
      <FormContainer>
        {formFields.map((field) => {
          if (field.name === "detailedAddress" || field.name === "address") {
            return null;
          }
          return (
            <FormWrapper key={field.name} ismobile={isMobile}>
              <FormKey ismobile={isMobile}>
                {field.label}
                {field.required && <b>&nbsp;*</b>}
              </FormKey>
              <div id="value">
                {field.type === "radio" ? (
                  field.options.map((option) => (
                    <label key={option}>
                      <input
                        type={field.type}
                        name={field.name}
                        value={option}
                        required={field.required}
                        checked={dataState[field.name] === option}
                        onChange={handleInputChange}
                      />
                      {option}
                    </label>
                  ))
                ) : field.name === "postalCode" ? (
                  <AddressWrapper ismobile={isMobile}>
                    <div>
                      <input
                        type={field.type}
                        name="postalCode"
                        id="postalCode"
                        placeholder="우편번호"
                        value={dataState[field.name] || ""}
                        readOnly
                        required
                      />
                      <input
                        type="button"
                        value="우편번호 찾기"
                        onClick={handleDaumClick}
                      />
                    </div>
                    <input
                      type="text"
                      name="address"
                      id="address"
                      placeholder="주소"
                      value={dataState["address"] || ""}
                      readOnly
                      required
                    />
                    <input
                      type="text"
                      name="detailedAddress"
                      id="detailAddress"
                      placeholder="상세주소"
                      value={dataState["detailedAddress"] || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </AddressWrapper>
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    required={field.required}
                    value={dataState[field.name] || ""}
                    onChange={handleInputChange}
                    placeholder={field.placeholder}
                  />
                )}
                {field.note && <span>{field.note}</span>}
              </div>
            </FormWrapper>
          );
        })}
      </FormContainer>
      <BtnContainer>
        <FormBtn type="button" onClick={handleCancelClick}>
          취소
        </FormBtn>
        <FormBtn
          id="submitButton"
          type="submit"
          onClick={handleSubmit}
          primary={isValid}
          disabled={!isValid || loading}
        >
          {mode === "apply" ? "신청완료" : "수정하기"}{" "}
          <i className="bi bi-arrow-right-circle-fill"></i>
        </FormBtn>
      </BtnContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  width: ${(props) => (props.ismobile ? "90%" : "82%")};
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding: 5rem 0;
  position: relative;
`;
const Title = styled.div`
  color: #555;
  font-family: "Jalnan", sans-serif;
  font-size: ${(props) => (props.ismobile ? "3rem" : "4rem")};
  font-style: normal;
  font-weight: 700;
  line-height: 100%;
  margin: 0 0 2rem;
  letter-spacing: -0.8px;
  & b {
    color: #ff791a;
  }
`;
const FormContainer = styled.div`
  overflow: hidden;
  border-radius: 15px;
  border: 1.5px solid #d9d9d9;
  width: 100%;
  display: flex;
  flex-direction: column;

  & > div {
    width: 100%;
    display: flex;
    align-items: center;
    border-bottom: 1.5px solid #d9d9d9;
    box-sizing: border-box;

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const FormWrapper = styled.div`
  padding: ${(props) => (props.ismobile ? "1rem" : "3.5rem")};
  width: 100%;
  display: flex;
  align-items: center;

  input[type="text"],
  input[type="tel"],
  input[type="date"],
  input[type="email"],
  input[type="password"] {
    color: #555;
    font-family: "Nanum Gothic", sans-serif;
    width: ${(props) => (props.ismobile ? "80%" : "30rem")};
    border: 1.2px solid #d9d9d9;
    margin-left: ${(props) => (props.ismobile ? "1rem" : "2rem")};
    padding: ${(props) => (props.ismobile ? "0.8rem" : "1.4rem")};
    font-size: ${(props) => (props.ismobile ? "1.2rem" : "1.5rem")};
  }
  input[type="radio"] {
    margin-left: 2rem;
  }
  &:last-child {
    border-bottom: 0;
  }
  & > #value {
    display: flex;
    flex-wrap: wrap;
    flex-direction: ${(props) => (props.ismobile ? "column" : "row")};
  }
  span {
    margin-left: ${(props) => (props.ismobile ? "1rem" : "2rem")};
    color: #555;
    font-size: ${(props) => (props.ismobile ? "1.0rem" : "1.3rem")};
    line-height: 100%;
    letter-spacing: -0.2px;
    margin-top: ${(props) => (props.ismobile ? "1rem" : "0")};
  }
`;

const FormKey = styled.div`
  width: ${(props) => (props.ismobile ? "30%" : "20%")};
  height: 100%;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  font-size: ${(props) => (props.ismobile ? "1.5rem" : "1.7rem")};
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
  letter-spacing: -0.2px;
  border-right: 1px solid #d9d9d9;
  flex-shrink: 0;
  & > b {
    color: #d10000;
  }
`;

const AddressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;

  & input[type="button"] {
    padding: ${(props) => (props.ismobile ? "1.0rem" : "1.4rem")};
    border: 1.2px solid #d9d9d9;
    background: #d9d9d9;
    color: #4f4f4f;
    font-size: ${(props) => (props.ismobile ? "1.2rem" : "1.5rem")};
    font-weight: 700;
    letter-spacing: -0.2px;
    cursor: pointer;
    margin-left: 1rem;
    margin-top: ${(props) => (props.ismobile ? "0.7rem" : 0)};
  }

  & input[type="text"]:first-child {
    width: ${(props) => (props.ismobile ? "80%" : "30rem")};
  }

  & input[type="text"] {
    width: 100%;
    font-size: ${(props) => (props.ismobile ? "1.2rem" : "1.5rem")};
  }
`;
const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  margin-top: 3.5rem;
`;
const FormBtn = styled.div`
  width: 50%;
  padding: 2rem;
  font-size: 1.9rem;
  font-weight: 700;
  background: #fff;
  border-radius: 100px 100px 100px 0px;
  border: 1.5px solid #d9d9d9;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 0 auto;
  cursor: pointer;
  ${(props) =>
    props.primary &&
    css`
      background: #ff791a;
      color: #fff;
    `}
`;
